// src/index.js

import React from 'react';
import { createRoot } from 'react-dom/client';    // <-- changed
import './quillNewsletterBlots'; // Register Quill blots before any editor mounts
import './index.css';
import './global.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { PrintQueueProvider } from './context/PrintQueueContext';
import LastActiveHeartbeat from './components/LastActiveHeartbeat';
import { logOdin } from './services/telemetry';

// Guard against initial FOUC/jumbled layout: hide root until fonts and CSS are ready
document.documentElement.classList.add('app-preload');
const fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready.catch(() => {}) : Promise.resolve();
const pageLoaded = new Promise((resolve) => {
    if (document.readyState === 'complete') return resolve();
    window.addEventListener('load', () => resolve(), { once: true });
});
Promise.race([
    Promise.all([fontsReady, pageLoaded]),
    new Promise((r) => setTimeout(r, 2500)) // hard timeout fallback
]).then(() => {
    requestAnimationFrame(() => document.documentElement.classList.remove('app-preload'));
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <AuthProvider>
            <LastActiveHeartbeat />
            <CartProvider>
                <PrintQueueProvider>
                    <App />
                </PrintQueueProvider>
            </CartProvider>
        </AuthProvider>
    </React.StrictMode>
);

// Global error boundary (window-level) and fetch interceptor to log failures
window.addEventListener('error', (e) => {
    try { logOdin('window.error', 'error', { message: e?.message, filename: e?.filename, lineno: e?.lineno, colno: e?.colno }); } catch {}
});
window.addEventListener('unhandledrejection', (e) => {
    try { logOdin('unhandledrejection', 'error', { reason: String(e?.reason) }); } catch {}
});

const origFetch = window.fetch;
window.fetch = async (...args) => {
    try {
        const res = await origFetch(...args);
        if (!res.ok) {
            try { logOdin('fetch_non_ok', 'warn', { url: String(args[0]), status: res.status }); } catch {}
        }
        return res;
    } catch (err) {
        try { logOdin('fetch_error', 'error', { url: String(args[0]), error: String(err) }); } catch {}
        throw err;
    }
};
